import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, Download, FileText, Inbox, MessagesSquare, Search, Send } from "lucide-react";
import { LeadThread } from "@/components/admin/LeadThread";
import AdminShell from "@/components/admin/AdminShell";
import { FollowUpDialog } from "@/components/admin/FollowUpDraft";
import { AutoFollowUp } from "@/components/admin/AutoFollowUp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, Empty, Spinner } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";
import { api, enc, errMsg, openApiPath } from "@/lib/api";
import { cn } from "@/lib/utils";

const STATUSES = ["all", "new", "contacted", "archived"];
const BANDS = [["all", "Any score"], ["low", "Under 60"], ["mid", "60-79"], ["high", "80+"]];
const inBand = (s, b) => b === "all" || (b === "low" ? s < 60 : b === "mid" ? s >= 60 && s < 80 : s >= 80);
const scoreCls = (s) => (s >= 80 ? "text-emerald-400 border-emerald-500/40" : s >= 60 ? "text-[#F3C649] border-[#D4AF37]/40" : "text-red-400 border-red-500/40");
const fmt = (d) => (d ? new Date(d).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");

const exportCsv = (rows) => {
  const cols = ["created_at", "score", "verdict", "status", "name", "email", "file_name", "summary"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = `resume-scans-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
};

const Chip = ({ on, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid} className={cn("rounded-full border px-3 py-1 text-xs capitalize transition-colors", on ? "border-[#D4AF37] bg-[#D4AF37] font-semibold text-black" : "border-[#27272A] text-zinc-400 hover:text-zinc-200")}>{children}</button>
);

const ScanRow = ({ r, onStatus, onFollowUp, open, onToggle, onChange }) => (
  <div className="rounded-xl border border-[#27272A] bg-[#09090B] p-4" data-testid={`scan-row-${r.scan_id}`}>
  <div className="flex flex-col gap-3 md:flex-row md:items-center">
    <span className={cn("flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 font-mono text-sm font-bold", scoreCls(r.score))}>{r.score}</span>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium text-zinc-100">{r.name || r.email || r.file_name || "Pasted resume"}</span>
        <span className="text-xs text-zinc-500">{r.verdict}</span>
      </div>
      {r.summary && <p className="mt-0.5 truncate text-xs text-zinc-400">{r.summary}</p>}
      <p className="mt-1 text-[11px] text-zinc-600">{fmt(r.created_at)}{r.email && r.name ? ` · ${r.email}` : ""}{r.source ? ` · ${r.source === "file" ? "uploaded file" : r.source === "text" ? "pasted text" : r.source.replace(/_/g, " ")}` : ""}</p>
      <div className="mt-1.5 flex flex-wrap gap-1.5 text-[10px]">
        {r.followed_up_at && <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-sky-300" data-testid={`scan-badge-followed-${r.scan_id}`}>Followed up {fmt(r.followed_up_at)}</span>}
        {r.nudged_at && <span className="rounded-full bg-violet-500/15 px-2 py-0.5 text-violet-300">Nudged</span>}
        {r.signed_up_at && <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-emerald-300" data-testid={`scan-badge-signed-${r.scan_id}`}>Signed up{r.plan && r.plan !== "free" ? ` · ${r.plan}` : ""}</span>}
        {r.existing_user && !r.signed_up_at && <span className="rounded-full bg-zinc-800 px-2 py-0.5 text-zinc-400">Existing user</span>}
        {r.unsubscribed && <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-red-300">Unsubscribed</span>}
      </div>
    </div>
    <div className="flex flex-wrap items-center gap-2">
      <ReplyButton r={r} open={open} onToggle={onToggle} />
      <Button size="sm" onClick={() => onFollowUp(r)} disabled={!r.email} title={r.email ? "" : "No email on this lead"} className="disabled:opacity-35" data-testid={`scan-followup-${r.scan_id}`}><Send className="h-3.5 w-3.5" />{!r.email ? "No email" : r.followed_up_at ? "View follow-up" : "Follow up"}</Button>
      <Button size="sm" variant="outline" onClick={() => openApiPath(() => api.get(`/admin/resume-scans/${enc(r.scan_id)}/resume`)).catch((e) => toast.error(errMsg(e)))} data-testid={`scan-resume-${r.scan_id}`}><FileText className="h-3.5 w-3.5" />Resume</Button>
      <select value={r.status || "new"} onChange={(e) => onStatus(r, e.target.value)} data-testid={`scan-status-${r.scan_id}`}
        className="h-9 rounded-md border border-[#27272A] bg-[#18181B] px-2 text-xs capitalize text-zinc-200">
        {STATUSES.slice(1).map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
    </div>
  </div>
  {r.reply_count > 0 && !open && r.last_reply_snippet && <p className="mt-2 truncate border-l-2 border-sky-500/40 pl-2 text-xs text-zinc-400" data-testid={`scan-reply-snippet-${r.scan_id}`}>“{r.last_reply_snippet}”</p>}
  {open && <div className="mt-4 border-t border-[#27272A] pt-4"><LeadThread lead={r} onChange={onChange} /></div>}
  </div>
);

const ReplyButton = ({ r, open, onToggle }) => (
  <Button size="sm" variant="outline" onClick={onToggle} disabled={!r.email} className={cn("disabled:opacity-35", r.unread_replies > 0 && "border-sky-500/50 text-sky-200")} data-testid={`scan-replies-${r.scan_id}`}>
    <MessagesSquare className="h-3.5 w-3.5" />{r.reply_count > 0 ? `${r.reply_count} repl${r.reply_count === 1 ? "y" : "ies"}` : "Thread"}
    {r.unread_replies > 0 && <span className="ml-0.5 rounded-full bg-sky-500 px-1.5 text-[10px] font-bold text-black" data-testid={`scan-unread-${r.scan_id}`}>{r.unread_replies}</span>}
    <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
  </Button>
);

export default function AdminScans() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState(null);
  const [missing, setMissing] = useState(false);
  const [status, setStatus] = useState("all");
  const [band, setBand] = useState("all");
  const [q, setQ] = useState("");
  const [lead, setLead] = useState(null);
  const [stats, setStats] = useState(null);
  const [replied, setReplied] = useState(false);
  const [openId, setOpenId] = useState(null);
  const patchRow = (id, u) => setItems((x) => x.map((i) => (i.scan_id === id ? { ...i, ...u } : i)));
  const unreadTotal = (items || []).reduce((n, r) => n + (r.unread_replies || 0), 0);

  useEffect(() => {
    api.get("/admin/resume-scans").then((r) => { setItems(r.data?.items || r.data || []); setStats(r.data?.stats || null); })
      .catch((e) => { if (e.response?.status === 404) setMissing(true); else toast.error(errMsg(e)); setItems([]); });
  }, []);

  if (!isAdmin) return <Navigate to="/search" replace />;

  const setRowStatus = async (r, s) => {
    const prev = items;
    setItems((x) => x.map((i) => (i.scan_id === r.scan_id ? { ...i, status: s } : i)));
    try { await api.patch(`/admin/resume-scans/${enc(r.scan_id)}`, { status: s }); } catch (e) { setItems(prev); toast.error(errMsg(e)); }
  };

  const ql = q.trim().toLowerCase();
  const rows = (items || [])
    .filter((r) => (status === "all" || (r.status || "new") === status) && inBand(r.score ?? 0, band) && (!replied || r.reply_count > 0))
    .filter((r) => !ql || [r.name, r.email, r.file_name, r.summary, r.verdict].some((v) => String(v || "").toLowerCase().includes(ql)))
    .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));

  return (
    <AdminShell title="Resume Scan Leads" subtitle="Every free resume scan, newest first."
      actions={<Button variant="outline" disabled={!rows.length} onClick={() => exportCsv(rows)} data-testid="scans-export-button"><Download className="h-4 w-4" />Export CSV</Button>}>
      {missing && <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200" data-testid="scans-backend-pending">Coming soon. This list fills in once the backend adds the admin resume-scan endpoints (backend spec section 19).</div>}
      <AutoFollowUp />
      {stats && <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="scan-stats">
        {[["Follow-ups sent", stats.sent], ["Auto-sent", stats.auto_sent], ["Nudged", stats.nudged], ["Signed up", stats.signed_up], ["Paid", stats.paid], ["Conversion", stats.conversion_rate == null ? "—" : `${Math.round(stats.conversion_rate <= 1 ? stats.conversion_rate * 100 : stats.conversion_rate)}%`]].map(([k, v]) => (
          <Card key={k} className="p-4" data-testid={`scan-stat-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}><div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div><div className="mt-1 font-display text-2xl text-zinc-50">{v ?? 0}</div></Card>
        ))}
      </div>}
      <Card className="mb-6 space-y-4">
        <div className="relative max-w-sm"><Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-500" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, file…" className="pl-9" data-testid="scans-search-input" /></div>
        <div className="flex flex-wrap gap-2">{STATUSES.map((s) => <Chip key={s} on={status === s} onClick={() => setStatus(s)} testid={`scans-status-${s}`}>{s}</Chip>)}
          <Chip on={replied} onClick={() => setReplied((v) => !v)} testid="scans-filter-replied">Replied{unreadTotal > 0 ? ` · ${unreadTotal} unread` : ""}</Chip></div>
        <div className="flex flex-wrap gap-2">{BANDS.map(([b, l]) => <Chip key={b} on={band === b} onClick={() => setBand(b)} testid={`scans-band-${b}`}>{l}</Chip>)}</div>
      </Card>
      {!items ? <Spinner className="h-6 w-6 text-[#D4AF37]" /> : rows.length === 0 ? (
        <Empty icon={Inbox} title="No scans yet" text={items.length ? "No scans match these filters." : "Free resume scans from the login page will appear here."} />
      ) : (
        <div className="space-y-2" data-testid="scans-list"><p className="text-xs text-zinc-500" data-testid="scans-count">{rows.length} scan{rows.length === 1 ? "" : "s"}</p>{rows.map((r) => <ScanRow key={r.scan_id} r={r} onStatus={setRowStatus} onFollowUp={setLead} open={openId === r.scan_id} onToggle={() => setOpenId((o) => (o === r.scan_id ? null : r.scan_id))} onChange={(u) => patchRow(r.scan_id, u)} />)}</div>
      )}
      <FollowUpDialog key={lead?.scan_id} lead={lead} onClose={() => setLead(null)} onSent={(u) => { setItems((x) => x.map((i) => (i.scan_id === u.scan_id ? { ...i, ...u } : i))); setLead(null); }} />
    </AdminShell>
  );
}
